import React, { useEffect, useState, createContext, useContext } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import api, { clearToken } from "./lib/api";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import BoardView from "./pages/BoardView";
import ErrorBoundary from "./components/ErrorBoundary";
import MobileNav from "./components/MobileNav";

// ====== Auth context ======
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearToken();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, logout, refresh: checkAuth }}>
      {children}
    </AuthCtx.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-black flex items-center justify-center animate-pulse">
            <span className="text-white font-mono text-sm font-bold">NT</span>
          </div>
          <div className="font-mono text-xs uppercase tracking-widest animate-pulse text-black/60">Memverifikasi sesi…</div>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function AppWithMobileNav() {
  const { user } = useAuth();
  const location = useLocation();
  const showMobileNav = user && location.pathname !== "/login" && location.pathname !== "/";

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/board/:boardId"
          element={
            <ProtectedRoute>
              <BoardView />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showMobileNav && <MobileNav />}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppWithMobileNav />
          <Toaster
            position="bottom-right"
            offset={20}
            toastOptions={{
              className: "font-mono text-xs uppercase tracking-wider border-2 border-black rounded-none",
              style: {
                borderRadius: 0,
                border: "2px solid #000",
                boxShadow: "4px 4px 0 0 #000",
                background: "#fff",
                color: "#000",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
